import { Suspense, lazy, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useNavigationType, useSearchParams } from "react-router-dom";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { MerchCartProvider } from "@/contexts/MerchCartContext";

import { OnboardingProvider } from "@/contexts/OnboardingContext";
import { TourLauncher } from "@/components/onboarding/TourLauncher";
import { PWAInstallProvider } from "@/contexts/PWAInstallContext";
import { PWAUpdateProvider } from "@/contexts/PWAUpdateContext";
import { PushNotificationProvider } from "@/contexts/PushNotificationContext";
import { useUserLanguage } from "@/hooks/useUserLanguage";
import { useCreditNotifications } from "@/hooks/useCreditNotifications";
import { useAuthSync } from "@/hooks/useAuthSync";
import { CookieConsentProvider } from "@/components/CookieConsent";
import { applySeasonalFavicon } from "@/lib/seasonalIcon";
import { AnalyticsProvider } from "@/components/AnalyticsProvider";
import { ChristmasThemeProvider } from "@/components/ChristmasThemeProvider";
import { SeasonalEventProvider } from "@/contexts/SeasonalEventContext";
import { NewYearThemeProvider } from "@/components/NewYearThemeProvider";
import { ValentineThemeProvider } from "@/components/ValentineThemeProvider";
import { HalloweenThemeProvider } from "@/components/HalloweenThemeProvider";
import { MobileBottomNav } from "@/components/MobileBottomNav";
import { ActiveStreamProvider } from "@/contexts/ActiveStreamContext";
import { PWAInstallBanner } from "@/components/PWAInstallBanner";
import { PWAUpdateManager } from "@/components/PWAUpdateManager";
import { HolidayPreviewChip } from "@/components/HolidayPreviewChip";
import { TrialBanner } from "@/components/TrialBanner";
import { OfflineUpgradeRequired } from "@/components/OfflineUpgradeRequired";
import { useOfflineMode } from "@/hooks/useOfflineMode";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { AdSenseLoader } from "@/components/AdSenseLoader";
import ErrorBoundary from "@/components/ErrorBoundary";
import { CloudStatusBanner } from "@/components/CloudStatusBanner";
import { AiQueueOverlay } from "@/components/AiQueueOverlay";
import { AiCostPreviewDialog } from "@/components/ai/AiCostPreviewDialog";
import { RecipeRedirect } from "@/components/RecipeRedirect";
import { DeferredMount } from "@/components/DeferredMount";

const SupportBubble = lazy(() =>
  import("@/components/support/SupportBubble").then((m) => ({ default: m.SupportBubble })),
);
const StaffPresenceTracker = lazy(() =>
  import("@/components/StaffPresenceTracker").then((m) => ({ default: m.StaffPresenceTracker })),
);
const LiveNowFloatingIndicator = lazy(() =>
  import("@/components/livestream/LiveNowFloatingIndicator").then((m) => ({
    default: m.LiveNowFloatingIndicator,
  })),
);
const CreatorScheduleFAB = lazy(() =>
  import("@/components/livestream/CreatorScheduleFAB").then((m) => ({ default: m.CreatorScheduleFAB })),
);
const GlobalCastButton = lazy(() =>
  import("@/components/livestream/GlobalCastButton").then((m) => ({ default: m.GlobalCastButton })),
);
const PersistentMiniPlayerBar = lazy(() =>
  import("@/components/livestream/PersistentMiniPlayerBar").then((m) => ({
    default: m.PersistentMiniPlayerBar,
  })),
);
const BadgeUnlockManager = lazy(() =>
  import("@/components/BadgeUnlockManager").then((m) => ({ default: m.BadgeUnlockManager })),
);
import { CreepyOverlay } from "@/components/halloween/CreepyOverlay";
const DailyCheckIn = lazy(() =>
  import("@/components/DailyCheckIn").then((m) => ({ default: m.DailyCheckIn })),
);

// Lazy load all page components for code splitting
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const ChaosKitchen = lazy(() => import("./pages/ChaosKitchen"));
const CursedMode = lazy(() => import("./pages/CursedMode"));
const AITools = lazy(() => import("./pages/AITools"));
const FlavorPairing = lazy(() => import("./pages/FlavorPairing"));
const Recipes = lazy(() => import("./pages/Recipes"));
const EasyDinner = lazy(() => import("./pages/EasyDinner"));
const RecipeDetail = lazy(() => import("./pages/RecipeDetail"));
const RecipeEdit = lazy(() => import("./pages/RecipeEdit"));
const RecipeCreate = lazy(() => import("./pages/RecipeCreate"));
const Profile = lazy(() => import("./pages/Profile"));
const Challenges = lazy(() => import("./pages/Challenges"));
const ChallengeDetail = lazy(() => import("./pages/ChallengeDetail"));
const Leaderboard = lazy(() => import("./pages/Leaderboard"));
const ShoppingList = lazy(() => import("./pages/ShoppingList"));
const MealPlanner = lazy(() => import("./pages/MealPlanner"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const TermsOfSale = lazy(() => import("./pages/TermsOfSale"));
const Returns = lazy(() => import("./pages/Returns"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Pricing = lazy(() => import("./pages/Pricing"));
const PromoUserVideo = lazy(() => import("./pages/PromoUserVideo"));
const PromoCreatorVideo = lazy(() => import("./pages/PromoCreatorVideo"));
const SubscriptionSettings = lazy(() => import("./pages/SubscriptionSettings"));
const CreditStore = lazy(() => import("./pages/CreditStore"));
const CreditHistory = lazy(() => import("./pages/CreditHistory"));
const CreditSuccess = lazy(() => import("./pages/CreditSuccess"));
const RecipeTransformer = lazy(() => import("./pages/RecipeTransformer"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AIUsageAnalytics = lazy(() => import("./pages/AIUsageAnalytics"));
const Community = lazy(() => import("./pages/Community"));
const FoodClubs = lazy(() => import("./pages/FoodClubs"));
const FoodClubDetail = lazy(() => import("./pages/FoodClubDetail"));
const FoodClubJoin = lazy(() => import("./pages/FoodClubJoin"));
const ClubKitOrders = lazy(() => import("./pages/ClubKitOrders"));
const AdventCalendarPage = lazy(() => import("./pages/AdventCalendar"));
const NotificationSettings = lazy(() => import("./pages/NotificationSettings"));
const NotificationHistory = lazy(() => import("./pages/NotificationHistory"));
const DownloadApp = lazy(() => import("./pages/DownloadApp"));
const Collections = lazy(() => import("./pages/Collections"));
const CollectionDetail = lazy(() => import("./pages/CollectionDetail"));
const Changelog = lazy(() => import("./pages/Changelog"));
const Feedback = lazy(() => import("./pages/Feedback"));
const OfflineRecipes = lazy(() => import("./pages/OfflineRecipes"));
const StreakRecoveryHistory = lazy(() => import("./pages/StreakRecoveryHistory"));
const RecipePrint = lazy(() => import("./pages/RecipePrint"));
const ShoppingListPrint = lazy(() => import("./pages/ShoppingListPrint"));
const FamilyAcceptInvite = lazy(() => import("./pages/FamilyAcceptInvite"));
const BulkAIActions = lazy(() => import("./pages/BulkAIActions"));
const LiveDiscovery = lazy(() => import("./pages/LiveDiscovery"));
const GoLive = lazy(() => import("./pages/GoLive"));
const LiveStreamPage = lazy(() => import("./pages/LiveStream"));
const StreamManager = lazy(() => import("./pages/StreamManager"));
const StreamAnalytics = lazy(() => import("./pages/StreamAnalytics"));
const StreamOverlay = lazy(() => import("./pages/StreamOverlay"));
const CreatorDashboard = lazy(() => import("./pages/CreatorDashboard"));
const CreatorMonetizationOverview = lazy(() => import("./pages/CreatorMonetizationOverview"));
const PlatformRiskChecklistPage = lazy(() => import("./pages/PlatformRiskChecklistPage"));
const StripeConnectAuditPage = lazy(() => import("./pages/StripeConnectAuditPage"));
const GiftPassStatsPage = lazy(() => import("./pages/GiftPassStatsPage"));
const BuyTokens = lazy(() => import("./pages/BuyTokens"));
const TokenHistory = lazy(() => import("./pages/TokenHistory"));
const CreatorAgreement = lazy(() => import("./pages/CreatorAgreement"));
const OBSPlugin = lazy(() => import("./pages/OBSPlugin"));
const ObsPluginConnect = lazy(() => import("./pages/ObsPluginConnect"));
const License = lazy(() => import("./pages/License"));
const Roadmap = lazy(() => import("./pages/Roadmap"));
const Support = lazy(() => import("./pages/Support"));
const RecipeBook = lazy(() => import("./pages/RecipeBook"));
const RecipeBookOrders = lazy(() => import("./pages/RecipeBookOrders"));
const Status = lazy(() => import("./pages/Status"));
const Clips = lazy(() => import("./pages/Clips"));
const ClipWatch = lazy(() => import("./pages/ClipWatch"));
const ClipMarkerTest = lazy(() => import("./pages/ClipMarkerTest"));
const Appeals = lazy(() => import("./pages/Appeals"));
const Transparency = lazy(() => import("./pages/Transparency"));
const TrustSafety = lazy(() => import("./pages/TrustSafety"));
const CommunityGuidelines = lazy(() => import("./pages/CommunityGuidelines"));
const StreamingPolicyPage = lazy(() => import("./pages/StreamingPolicy"));
const DMCAPage = lazy(() => import("./pages/DMCA"));
const HelperApplication = lazy(() => import("./pages/HelperApplication"));
const HelperSupport = lazy(() => import("./pages/HelperSupport"));
const ReportPage = lazy(() => import("./pages/Report"));
const CreatorTipPage = lazy(() => import("./pages/CreatorTipPage"));
const Advertise = lazy(() => import("./pages/Advertise"));
const AdKit = lazy(() => import("./pages/AdKit"));
const Merch = lazy(() => import("./pages/Merch"));
const HalloweenMerch = lazy(() => import("./pages/HalloweenMerch"));
const HalloweenNightPage = lazy(() => import("./pages/HalloweenNightPage"));
const GiftRedeem = lazy(() => import("./pages/GiftRedeem"));
function LegacyGiftRedirect() { const [sp] = useSearchParams(); const s = sp.toString(); return <Navigate to={s ? `/gift?${s}` : "/gift"} replace />; }
const HalloweenCookalong = lazy(() => import("./pages/HalloweenCookalong"));
const CreatorStore = lazy(() => import("./pages/CreatorStore"));
const QuickMeals = lazy(() => import("./pages/QuickMeals"));
const SubstitutionFinder = lazy(() => import("./pages/SubstitutionFinder"));
const AIWorkbenchTool = lazy(() => import("./pages/AIWorkbenchTool"));
const UnitConverter = lazy(() => import("./pages/UnitConverter"));
const MultiTimer = lazy(() => import("./pages/MultiTimer"));
const DefectClaim = lazy(() => import("./pages/DefectClaim"));
const PantryTracker = lazy(() => import("./pages/PantryTracker"));
const UseWhatIHave = lazy(() => import("./pages/UseWhatIHave"));
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 10 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Scroll to top on forward navigations; preserve position on browser back/forward.
const ScrollToTop = () => {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  useEffect(() => {
    if (navigationType === "POP") return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, navigationType]);
  return null;
};

// Seasonal browser-tab icon: Halloween while the theme is on, default after the Nov 1 06:00 deadline.
const SeasonalFavicon = () => {
  useEffect(() => {
    applySeasonalFavicon();
    const id = window.setInterval(() => applySeasonalFavicon(), 60_000);
    return () => window.clearInterval(id);
  }, []);
  return null;
};

// AdSense + IMA SDK guard — prevents ad scripts from loading on OBS overlay routes
const AdSenseLoaderGuard = () => {
  const location = useLocation();
  const isOverlay = location.pathname.startsWith('/overlay/');

  // Dynamically inject IMA SDK on non-overlay routes (for video ads)
  // Deferred until the browser is idle so it never competes with first paint.
  useEffect(() => {
    if (isOverlay) return;
    const load = () => {
      if (document.querySelector('script[src*="imasdk.googleapis.com"]')) return;
      const script = document.createElement('script');
      script.src = 'https://imasdk.googleapis.com/js/sdkloader/ima3.js';
      script.async = true;
      document.head.appendChild(script);
    };
    const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number };
    if (w.requestIdleCallback) w.requestIdleCallback(load, { timeout: 6000 });
    else setTimeout(load, 4000);
  }, [isOverlay]);

  if (isOverlay) return null;
  return <AdSenseLoader />;
};

// Loading fallback — top progress bar
const PageLoader = () => (
  <div className="min-h-[50vh] bg-background relative overflow-hidden">
    <div className="fixed top-0 left-0 right-0 z-[9999] h-1 overflow-hidden">
      <div className="h-full bg-gradient-to-r from-primary via-secondary to-primary animate-shimmer" style={{ backgroundSize: '200% 100%' }} />
    </div>
    <div className="container max-w-3xl mx-auto px-4 pt-16 space-y-4 animate-pulse">
      <div className="h-8 w-2/3 rounded-lg bg-muted/40" />
      <div className="h-4 w-full rounded bg-muted/30" />
      <div className="h-4 w-5/6 rounded bg-muted/25" />
      <div className="h-40 w-full rounded-xl bg-muted/20 mt-6" />
    </div>
  </div>
);

// Trial banner wrapper that uses subscription context
const TrialBannerWrapper = () => {
  const { trial } = useSubscription();
  
  if (!trial.isOnTrial || !trial.trialTier) return null;
  
  return (
    <div className="container max-w-4xl mx-auto px-4 pt-4">
      <TrialBanner
        trialTier={trial.trialTier}
        daysRemaining={trial.daysRemaining}
        trialEndDate={trial.trialEndDate || ''}
      />
    </div>
  );
};

/** Old kitchen URL — keep query so cursed mood / advent theme still apply. */
const LegacyChaosKitchenRedirect = () => {
  const [searchParams] = useSearchParams();
  const search = searchParams.toString();
  return <Navigate to={search ? `/chaos?${search}` : "/chaos"} replace />;
};

const AppContent = () => {
  useUserLanguage();
  useCreditNotifications();
  useAuthSync();
  
  const location = useLocation();
  const { showUpgradeOverlay, isLoggedIn } = useOfflineMode();
  
  // Overlay routes must render without any site chrome (for OBS/Streamlabs browser sources)
  const isOverlayRoute = location.pathname.startsWith('/overlay/');
  
  // Skip offline overlay for print and family-accept routes
  const isPrintRoute = location.pathname.includes('/print');
  const isFamilyAcceptRoute = location.pathname.startsWith('/family/accept');
  const skipOfflineOverlay = isPrintRoute || isFamilyAcceptRoute || isOverlayRoute;
  
  return (
    <>
      {/* Cloud status banner - skip on overlay routes */}
      {!isOverlayRoute && <CloudStatusBanner />}
      {!isOverlayRoute && <AiQueueOverlay />}
      {!isOverlayRoute && <AiCostPreviewDialog />}

      {/* Offline upgrade overlay for free users - skip on print/overlay routes */}
      {showUpgradeOverlay && !skipOfflineOverlay && <OfflineUpgradeRequired isLoggedIn={isLoggedIn} />}

      {/* Site chrome — hidden on overlay routes for clean OBS browser sources */}
      {!isOverlayRoute && <TourLauncher />}
      {!isOverlayRoute && <TrialBannerWrapper />}

      <div className={isOverlayRoute ? undefined : "pb-16 md:pb-0"}>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
            <Route path="/chaos" element={<ChaosKitchen />} />
            <Route path="/chaos-kitchen" element={<LegacyChaosKitchenRedirect />} />
            <Route path="/fix-my-dish" element={<CursedMode />} />
            <Route path="/cursed" element={<Navigate to="/fix-my-dish" replace />} />
            <Route path="/ai-tools" element={<AITools />} />
            <Route path="/flavor-pairing" element={<FlavorPairing />} />
            <Route path="/transform" element={<RecipeTransformer />} />
            <Route path="/recipes" element={<Recipes />} />
            <Route path="/nem-aftensmad" element={<EasyDinner />} />
            <Route path="/easy-dinner" element={<EasyDinner />} />
            {/* Short recipe URLs */}
            <Route path="/r/create" element={<RecipeCreate />} />
            <Route path="/r/:slug" element={<RecipeDetail />} />
            <Route path="/r/:slug/print" element={<RecipePrint />} />
            <Route path="/r/:slug/edit" element={<RecipeEdit />} />
            {/* Legacy redirects */}
            <Route path="/recipes/create" element={<Navigate to="/r/create" replace />} />
            <Route path="/recipes/:slug" element={<RecipeRedirect />} />
            <Route path="/recipes/:slug/print" element={<RecipeRedirect />} />
            <Route path="/recipes/:slug/edit" element={<RecipeRedirect />} />
            <Route path="/recipe/:slug" element={<RecipeRedirect />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/profile/:username" element={<Profile />} />
            <Route path="/chef/:username" element={<Profile />} />
            <Route path="/challenges" element={<Challenges />} />
            <Route path="/challenges/:slug" element={<ChallengeDetail />} />
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/community" element={<Community />} />
            <Route path="/foodclub" element={<FoodClubs />} />
            <Route path="/madklub" element={<FoodClubs />} />
            <Route path="/foodclub/kit-orders" element={<ClubKitOrders />} />
            <Route path="/foodclub/join/:code" element={<FoodClubJoin />} />
            <Route path="/madklub/join/:code" element={<FoodClubJoin />} />
            <Route path="/foodclub/:slug" element={<FoodClubDetail />} />
            <Route path="/advent" element={<AdventCalendarPage />} />
            <Route path="/shopping-list/print" element={<ShoppingListPrint />} />
            <Route path="/shopping-list" element={<ShoppingList />} />
            <Route path="/meal-planner" element={<MealPlanner />} />
            <Route path="/madplan" element={<Navigate to="/meal-planner" replace />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/promo/user" element={<PromoUserVideo />} />
            <Route path="/promo/creator" element={<PromoCreatorVideo />} />
            <Route path="/subscription" element={<SubscriptionSettings />} />
            <Route path="/subscription-settings" element={<SubscriptionSettings />} />
            <Route path="/credits" element={<CreditStore />} />
            <Route path="/credits/history" element={<CreditHistory />} />
            <Route path="/credits/success" element={<CreditSuccess />} />
            <Route path="/ai-analytics" element={<AIUsageAnalytics />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/settings/notifications" element={<NotificationSettings />} />
            <Route path="/settings/notification-history" element={<NotificationHistory />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/terms-of-sale" element={<TermsOfSale />} />
            <Route path="/returns" element={<Returns />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/download" element={<DownloadApp />} />
            <Route path="/collections" element={<Collections />} />
            <Route path="/collections/:id" element={<CollectionDetail />} />
            <Route path="/changelog" element={<Changelog />} />
            <Route path="/feedback" element={<Feedback />} />
            <Route path="/offline-recipes" element={<OfflineRecipes />} />
            <Route path="/streak-history" element={<StreakRecoveryHistory />} />
            <Route path="/family/accept" element={<FamilyAcceptInvite />} />
            <Route path="/bulk-actions" element={<BulkAIActions />} />
            <Route path="/live" element={<LiveDiscovery />} />
            <Route path="/live/go" element={<GoLive />} />
            <Route path="/live/obs-plugin" element={<OBSPlugin />} />
            <Route path="/live/obs-plugin/connect" element={<ObsPluginConnect />} />
            <Route path="/live/clip-test" element={<ClipMarkerTest />} />
            <Route path="/go-live" element={<Navigate to="/live/go" replace />} />
            <Route path="/live/manager/:username" element={<StreamManager />} />
            <Route path="/live/analytics/:username" element={<StreamAnalytics />} />
            <Route path="/live/:id" element={<LiveStreamPage />} />
            <Route path="/overlay/:type/:streamId" element={<StreamOverlay />} />
            <Route path="/creator/dashboard" element={<CreatorDashboard />} />
            <Route
              path="/creator/settings"
              element={<Navigate to={{ pathname: "/creator/dashboard", hash: "settings" }} replace />}
            />
            <Route path="/creator/monetization-overview" element={<CreatorMonetizationOverview />} />
            <Route path="/creator/agreement" element={<CreatorAgreement />} />
            <Route path="/admin/risk-checklist" element={<PlatformRiskChecklistPage />} />
            <Route path="/admin/stripe-connect-audit" element={<StripeConnectAuditPage />} />
            <Route path="/admin/gift-passes" element={<GiftPassStatsPage />} />
            <Route path="/tokens/buy" element={<BuyTokens />} />
            <Route path="/tokens/history" element={<TokenHistory />} />
            <Route path="/roadmap" element={<Roadmap />} />
            <Route path="/support" element={<Support />} />
            <Route path="/license" element={<License />} />
            <Route path="/recipe-book" element={<RecipeBook />} />
            <Route path="/recipe-book/orders" element={<RecipeBookOrders />} />
            <Route path="/status" element={<Status />} />
            <Route path="/clips" element={<Clips />} />
            <Route path="/clip/:id" element={<ClipWatch />} />
            <Route path="/appeals" element={<Appeals />} />
            <Route path="/transparency" element={<Transparency />} />
            <Route path="/trust-safety" element={<TrustSafety />} />
            <Route path="/guidelines" element={<CommunityGuidelines />} />
            <Route path="/streaming-policy" element={<StreamingPolicyPage />} />
            <Route path="/dmca" element={<DMCAPage />} />
            <Route path="/report" element={<ReportPage />} />
            <Route path="/apply/helper" element={<HelperApplication />} />
            <Route path="/helper/support" element={<HelperSupport />} />
            <Route path="/tip/:username" element={<CreatorTipPage />} />
            <Route path="/advertise" element={<Advertise />} />
            <Route path="/adkit" element={<AdKit />} />
            <Route path="/merch" element={<Merch />} />
            <Route path="/merch/halloween" element={<HalloweenMerch />} />
            <Route path="/halloween-night" element={<HalloweenNightPage />} />
            <Route path="/gift" element={<GiftRedeem />} />
            <Route path="/gift/halloween" element={<LegacyGiftRedirect />} />
            <Route path="/halloween" element={<HalloweenCookalong />} />
            <Route path="/merch/:username" element={<CreatorStore />} />
            <Route path="/quick-meals" element={<QuickMeals />} />
            <Route path="/substitutions" element={<SubstitutionFinder />} />
            <Route path="/ai/:toolSlug" element={<AIWorkbenchTool />} />
            <Route path="/convert" element={<UnitConverter />} />
            <Route path="/timers" element={<MultiTimer />} />
            <Route path="/defect-claim/:orderId" element={<DefectClaim />} />
            <Route path="/pantry" element={<PantryTracker />} />
            <Route path="/spisekammer" element={<Navigate to="/pantry" replace />} />
            <Route path="/use-what-i-have" element={<UseWhatIHave />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </div>
      {!isOverlayRoute && (
        <>
          <Suspense fallback={null}>
            <DailyCheckIn />
          </Suspense>
          <CreepyOverlay />
          <DeferredMount>
            <Suspense fallback={null}>
              <BadgeUnlockManager />
              <StaffPresenceTracker />
              <SupportBubble />
              <LiveNowFloatingIndicator />
              <CreatorScheduleFAB />
              <GlobalCastButton />
              <PersistentMiniPlayerBar />
            </Suspense>
          </DeferredMount>
          <MobileBottomNav />
          <PWAInstallBanner />
          <PWAUpdateManager />
          <HolidayPreviewChip />
        </>
      )}
    </>
  );
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <SubscriptionProvider>
          <PWAInstallProvider>
            <PWAUpdateProvider>
              <PushNotificationProvider>
                <TooltipProvider>
                  <Toaster />
                  <Sonner />
                  <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                    <MerchCartProvider>
                    <CookieConsentProvider>
                      <ScrollToTop />
                      <SeasonalFavicon />
                      <AdSenseLoaderGuard />
                      <AnalyticsProvider>
                        <SeasonalEventProvider>
                          <NewYearThemeProvider>
                            <ChristmasThemeProvider>
                              <ValentineThemeProvider>
                                <HalloweenThemeProvider>
                                  <OnboardingProvider>
                                    <ActiveStreamProvider>
                                      <AppContent />
                                    </ActiveStreamProvider>
                                  </OnboardingProvider>
                                </HalloweenThemeProvider>
                              </ValentineThemeProvider>
                            </ChristmasThemeProvider>
                          </NewYearThemeProvider>
                        </SeasonalEventProvider>
                      </AnalyticsProvider>
                     </CookieConsentProvider>
                    </MerchCartProvider>
                  </BrowserRouter>
                </TooltipProvider>
              </PushNotificationProvider>
            </PWAUpdateProvider>
          </PWAInstallProvider>
      </SubscriptionProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;